import { OrbitControls } from '@react-three/drei';
import { Canvas as CanvasUI } from '@react-three/fiber';
import { Suspense, useEffect, useRef, useState } from 'react';
import { ROOT_MEDIA } from 'shared-base';
import { ObjectPlaceholder } from './_parts/ObjectPlaceholder';
import { Wrapper } from './SceneBuilder.style';
// import { Bloom, EffectComposer, Vignette } from '@react-three/postprocessing';
import { useCustomEvent } from '@sloyd/ui';
import classnames from 'classnames';
import { Character } from './_parts/Character';
import { OrbitTargetSetter } from './_parts/OrbitTargetSetter';
import { RemoteObject } from './_parts/RemoteObject';
import { TiledFloor } from './_parts/TiledFloor';
import { TiledWall } from './_parts/TiledWall';

export type SceneBuilderProps = {
  characters: ISceneItem[];
  sceneItems: ISceneItem[];
  sceneBackgroundOn: boolean;
  editMode: 'scene' | 'live';
  children?: React.ReactNode;
  callbacks: {
    setTileArea: (value?: Area) => void;
  };
};

export function SceneBuilder(props: SceneBuilderProps) {
  const { sceneItems, sceneBackgroundOn, characters, editMode, callbacks } = props;
  const [showFloor, setShowFloor] = useState(true);

  const orbitRef = useRef<any>();
  const [dragInProgress, setDragInProgress] = useState(false);

  useEffect(() => {
    if (orbitRef.current) {
      orbitRef.current.enabled = !dragInProgress;
    }
  }, [dragInProgress]);

  useCustomEvent('TOGGLE_FLOOR', (ev) => {
    const { show } = ev;
    setShowFloor(show);
  });

  function renderWalls() {
    return null;
    return (
      <>
        <TiledWall position={[-3 - 0.3, 2.5 - 0.25, 0]} rotation={[0, 90, 0]} />
        <TiledWall position={[0, 2.5 - 0.25, -3 - 0.3]} rotation={[0, 0, 0]} />
      </>
    );
  }

  function renderSceneItem(item: ISceneItem) {
    const { url, position, rotation, scale } = item;

    return (
      <RemoteObject key={item.id} url={url} position={position} rotation={rotation} scale={scale} />
    );
  }

  function renderSceneItems() {
    if (sceneBackgroundOn) return null;

    return sceneItems.map((item: ISceneItem) => renderSceneItem(item));
  }

  function renderCharacter(character: ISceneItem) {
    const { url, scale, position, rotation } = character;

    return (
      <Character
        key={character.id}
        url={url}
        scale={scale}
        position={position}
        rotation={rotation}
      />
    );
  }

  function renderCharacters() {
    return characters.map((character: ISceneItem) => renderCharacter(character));
  }

  function renderFloor() {
    if (!showFloor || editMode === 'live' || sceneBackgroundOn) {
      return null;
    }

    return (
      <TiledFloor
        onTileAreaChange={(area) => {
          callbacks.setTileArea(area);
        }}
      />
    );
  }

  function renderLights() {
    return (
      <>
        {/* --- Three-point lighting setup with warm tones --- */}
        <ambientLight intensity={1} color='#ffe5cc' />

        {/* Key Light (strongest) */}
        <directionalLight
          position={[5, 10, 5]}
          intensity={4}
          color='#ffcc99'
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
          shadow-camera-near={1}
          shadow-camera-far={20}
        />

        {/* Fill Light (softer, from camera direction) */}
        <pointLight position={[4, 4, 4]} intensity={1} color='#ffd6a0' />

        {/* Rim/Back Light - You could also warm this up or keep it cool for contrast */}
        <directionalLight position={[-5, 5, -5]} intensity={2} color='#ffaa77' />

        {/* Side Fill Light (from the left, casting shadows to the right) */}
        <directionalLight
          position={[-10, 12, 5]}
          intensity={1}
          color='#ffbb66'
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
          shadow-camera-near={1}
          shadow-camera-far={20}
        />
      </>
    );
  }

  const styleTransferStyle = 'ST-005';

  const style = sceneBackgroundOn
    ? {
        backgroundImage: `url(${ROOT_MEDIA}/style-transfer/ST-010t.png)`,
      }
    : {};

  const className = classnames('SceneBuilder-wrapper', {
    stylizedBackgroundOn: sceneBackgroundOn,
  });

  return (
    <Wrapper style={style} className={className} data-testid='SceneBuilder-wrapper'>
      <CanvasUI
        orthographic
        dpr={[1, 1.5]}
        camera={{ zoom: 100, position: [4, 4, 4], up: [0, 1, 0] }}
        shadows
        gl={{
          preserveDrawingBuffer: true,
        }}
      >
        <Suspense fallback={<></>}>
          {/* {renderLights()} */}
          {/* <Backdrop url={`${ROOT_MEDIA}/style-transfer/${styleTransferStyle}.png`} /> */}
          <OrbitControls ref={orbitRef} makeDefault enableZoom={true} enablePan={true} />

          {renderWalls()}
          {renderFloor()}
          {false && (
            <ObjectPlaceholder position={[2, 0, 1]} dimension={[2, 1, 1]} placeholder='good' />
          )}
          {renderLights()}
          {renderSceneItems()}
          {renderCharacters()}
          {/* <Sparkles count={100} scale={2} size={6} speed={0.4} /> */}

          {renderCharacters()}
          <OrbitTargetSetter />
          {/* <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} /> */}
        </Suspense>
      </CanvasUI>
      {props.children}
    </Wrapper>
  );
}

export default SceneBuilder;
